import { omit } from "@msys/common";
import type {
  AddressInput,
  PhoneType,
} from "../../../../clients/graphqlTypes.js";
import type { OnboardingWebsitePrefillData } from "./OnboardingFlowWebsitePrefill.js";

export interface OnboardingPhone {
  main: boolean;
  type: PhoneType;
  number: string;
}

/** The company logo as the form holds it: what `updateOrganisation.logo` takes. */
export interface OnboardingLogo {
  url: string;
  title?: string | null;
  mimeType?: string | null;
}

export interface OnboardingFormValues {
  website: string | null;
  fullLegalName: string;
  address: AddressInput;
  email: string | null;
  /**
   * The step edits the first phone only; every phone after it is carried along
   * untouched, so saving never drops a number the organisation already has.
   */
  phones: OnboardingPhone[];
  managingDirector: string;
  vatNumber: string;
  taxNumber: string;
  /** The trade register number. */
  registrationNumber: string;
  logo: OnboardingLogo | null;
  /**
   * The property owner's optional name for the property; sent as the
   * organisation `title` only when filled. Unused by the business step.
   */
  propertyName?: string;
}

const isBlank = (value: string | null | undefined): boolean =>
  !value || value.trim() === "";

// A country code alone does not make an address: the form starts with the
// country preset while street, postal code and city are still unfilled.
const isAddressEmpty = (address: AddressInput): boolean =>
  isBlank(address.streetLines1) &&
  isBlank(address.postalCode) &&
  isBlank(address.city);

const isTextReplaceable = (
  current: string | null,
  initial: string | null
): boolean => isBlank(current) || current === initial;

const isAddressUnchanged = (
  current: AddressInput,
  initial: AddressInput
): boolean =>
  current.streetLines1 === initial.streetLines1 &&
  current.postalCode === initial.postalCode &&
  current.city === initial.city &&
  current.countryCode === initial.countryCode;

/**
 * Applies a website prefill to the onboarding form without overwriting what
 * the user entered. A prefilled field is written only when the form value is
 * empty or still holds the value the form started with (`initial`: a default
 * such as the organisation name, or data already stored on the organisation),
 * since neither was typed by the user. The managing director follows the same
 * rule; the logo and the tax and legal details are never touched, because the
 * lookup does not find them. The address is merged as one unit
 * (never part by part), only the first phone is filled (the others stay as
 * they are), and the website is always written because it is the lookup input.
 */
export function mergePrefillIntoFormValues(
  current: OnboardingFormValues,
  { website, prefill }: OnboardingWebsitePrefillData,
  initial: OnboardingFormValues
): OnboardingFormValues {
  const [firstPhone, ...otherPhones] = current.phones;
  const phoneReplaceable =
    !firstPhone ||
    isTextReplaceable(firstPhone.number, initial.phones[0]?.number ?? null);
  const addressReplaceable =
    isAddressEmpty(current.address) ||
    isAddressUnchanged(current.address, initial.address);

  return {
    ...current,
    website,
    fullLegalName: isTextReplaceable(
      current.fullLegalName,
      initial.fullLegalName
    )
      ? (prefill.fullLegalName ?? current.fullLegalName)
      : current.fullLegalName,
    email: isTextReplaceable(current.email, initial.email)
      ? (prefill.email ?? current.email)
      : current.email,
    managingDirector: isTextReplaceable(
      current.managingDirector,
      initial.managingDirector
    )
      ? (prefill.managingDirector ?? current.managingDirector)
      : current.managingDirector,
    phones:
      phoneReplaceable && prefill.phone
        ? [
            firstPhone
              ? { ...firstPhone, number: prefill.phone }
              : { main: true, type: "WORK", number: prefill.phone },
            ...otherPhones,
          ]
        : current.phones,
    address:
      addressReplaceable && prefill.address
        ? {
            ...omit(prefill.address, "__typename", "location"),
            location: prefill.address.location
              ? omit(prefill.address.location, "__typename")
              : null,
          }
        : current.address,
  };
}

/**
 * The values a later lookup may still replace. A field a lookup has just
 * written counts as untouched — like the form's starting value — until the
 * user edits it, so looking up a corrected website replaces the first result
 * while what the user typed in between stays. Fields the lookup did not write
 * keep their previous untouched value.
 */
export function trackPrefilledValues(
  untouched: OnboardingFormValues,
  before: OnboardingFormValues,
  after: OnboardingFormValues
): OnboardingFormValues {
  const [firstPhoneAfter] = after.phones;
  const phoneWritten =
    firstPhoneAfter !== undefined &&
    firstPhoneAfter.number !== before.phones[0]?.number;

  return {
    ...untouched,
    fullLegalName:
      after.fullLegalName !== before.fullLegalName
        ? after.fullLegalName
        : untouched.fullLegalName,
    email: after.email !== before.email ? after.email : untouched.email,
    managingDirector:
      after.managingDirector !== before.managingDirector
        ? after.managingDirector
        : untouched.managingDirector,
    phones: phoneWritten
      ? [firstPhoneAfter, ...untouched.phones.slice(1)]
      : untouched.phones,
    address: isAddressUnchanged(after.address, before.address)
      ? untouched.address
      : after.address,
  };
}

/**
 * A field of the business step that a website lookup can fill. The address is
 * written as one unit, but each of its text parts is suggested (and restored)
 * on its own.
 */
export type SuggestedField =
  | "fullLegalName"
  | "email"
  | "phone"
  | "street"
  | "postalCode"
  | "city"
  | "managingDirector";

/** `suggested`: still holds the lookup's value. `edited`: the user changed a suggested field. */
export type SuggestionState = "suggested" | "edited";

const firstPhoneNumber = (values: OnboardingFormValues): string =>
  values.phones[0]?.number ?? "";

const fieldValue = (
  field: SuggestedField,
  values: OnboardingFormValues
): string => {
  switch (field) {
    case "fullLegalName":
      return values.fullLegalName;
    case "email":
      return values.email ?? "";
    case "managingDirector":
      return values.managingDirector;
    case "phone":
      return firstPhoneNumber(values);
    case "street":
      return values.address.streetLines1;
    case "postalCode":
      return values.address.postalCode;
    case "city":
      return values.address.city;
  }
};

const isFieldEqual = (
  field: SuggestedField,
  a: OnboardingFormValues,
  b: OnboardingFormValues
): boolean => {
  // A missing (null) email and an empty one are the same value here.
  return fieldValue(field, a) === fieldValue(field, b);
};

const SUGGESTED_FIELDS: SuggestedField[] = [
  "fullLegalName",
  "email",
  "phone",
  "street",
  "postalCode",
  "city",
  "managingDirector",
];

/** The fields a lookup wrote: those whose value differs between `before` and `after`. */
export function getSuggestedFields(
  before: OnboardingFormValues,
  after: OnboardingFormValues
): SuggestedField[] {
  return SUGGESTED_FIELDS.filter(field => !isFieldEqual(field, before, after));
}

/**
 * The suggestions that still stand: a field stops being a suggestion once its
 * value differs from the one the lookup wrote (`applied`), i.e. once the user
 * edited it.
 */
export function removeEditedSuggestions(
  suggested: readonly SuggestedField[],
  applied: OnboardingFormValues,
  current: OnboardingFormValues
): SuggestedField[] {
  return suggested.filter(field => isFieldEqual(field, applied, current));
}

/**
 * Per field a lookup filled: still `suggested`, or `edited` since. Fields the
 * lookup never filled have no entry.
 */
export function getSuggestionStates(
  filled: readonly SuggestedField[],
  applied: OnboardingFormValues,
  current: OnboardingFormValues
): Partial<Record<SuggestedField, SuggestionState>> {
  const states: Partial<Record<SuggestedField, SuggestionState>> = {};
  for (const field of filled) {
    states[field] = isFieldEqual(field, applied, current)
      ? "suggested"
      : "edited";
  }
  return states;
}

const ADDRESS_PARTS: SuggestedField[] = ["street", "postalCode", "city"];

const addressWith = (
  address: AddressInput,
  field: SuggestedField,
  source: AddressInput
): AddressInput => {
  switch (field) {
    case "street":
      return { ...address, streetLines1: source.streetLines1 };
    case "postalCode":
      return { ...address, postalCode: source.postalCode };
    case "city":
      return { ...address, city: source.city };
    default:
      return address;
  }
};

/**
 * Writes one suggested field back to the value the lookup wrote (`applied`).
 * The address regains its location (it counts as confirmed again) as soon as
 * street, postcode, city and country all match the lookup's address again;
 * while one part differs it stays unconfirmed.
 */
export function restoreSuggestedField(
  current: OnboardingFormValues,
  applied: OnboardingFormValues,
  field: SuggestedField
): OnboardingFormValues {
  switch (field) {
    case "fullLegalName":
      return { ...current, fullLegalName: applied.fullLegalName };
    case "email":
      return { ...current, email: applied.email };
    case "managingDirector":
      return { ...current, managingDirector: applied.managingDirector };
    case "phone": {
      const [firstPhone, ...otherPhones] = current.phones;
      return {
        ...current,
        phones: [
          firstPhone
            ? { ...firstPhone, number: firstPhoneNumber(applied) }
            : { main: true, type: "WORK", number: firstPhoneNumber(applied) },
          ...otherPhones,
        ],
      };
    }
    case "street":
    case "postalCode":
    case "city": {
      const address = addressWith(current.address, field, applied.address);
      const matchesLookup =
        isAddressUnchanged(address, applied.address) &&
        address.countryCode === applied.address.countryCode;
      return {
        ...current,
        address: {
          ...address,
          location: matchesLookup ? (applied.address.location ?? null) : null,
        },
      };
    }
  }
}

/** Puts the company name back to what the user had entered before the lookup. */
export function restoreUserInput(
  current: OnboardingFormValues,
  before: OnboardingFormValues
): OnboardingFormValues {
  return { ...current, fullLegalName: before.fullLegalName };
}

export interface RestoreDescription {
  /** `suggestion`: back to the lookup's value; `input`: back to the user's own input. */
  kind: "suggestion" | "input";
  value: string;
}

/**
 * What the restore button of a field offers, or `null` when it shows none. An
 * edited field offers the suggestion. The company name starts out as the user's
 * registration input, so once the lookup replaced it, it offers that input
 * instead and nothing after being edited or restored.
 */
export function describeRestore(
  field: SuggestedField,
  state: SuggestionState | undefined,
  {
    before,
    applied,
  }: { before: OnboardingFormValues; applied: OnboardingFormValues }
): RestoreDescription | null {
  if (field === "fullLegalName") {
    return state === "suggested"
      ? { kind: "input", value: fieldValue(field, before) }
      : null;
  }
  return state === "edited"
    ? { kind: "suggestion", value: fieldValue(field, applied) }
    : null;
}

/**
 * Takes the suggested fields back to their values from before the lookup. What
 * the user typed (a field that is not a suggestion) stays, and so does the type
 * of the first phone. The website goes back too: it was the lookup's input.
 * Restoring all three address parts restores the whole address (country and
 * location included); a part alone leaves the address unconfirmed.
 */
export function undoPrefill(
  current: OnboardingFormValues,
  before: OnboardingFormValues,
  suggested: readonly SuggestedField[]
): OnboardingFormValues {
  const restores = (field: SuggestedField) => suggested.includes(field);
  const [firstPhone, ...otherPhones] = current.phones;
  const restoredParts = ADDRESS_PARTS.filter(restores);
  const address =
    restoredParts.length === ADDRESS_PARTS.length
      ? before.address
      : restoredParts.length > 0
        ? {
            ...restoredParts.reduce(
              (result, part) => addressWith(result, part, before.address),
              current.address
            ),
            location: null,
          }
        : current.address;

  return {
    ...current,
    website: before.website,
    fullLegalName: restores("fullLegalName")
      ? before.fullLegalName
      : current.fullLegalName,
    email: restores("email") ? before.email : current.email,
    managingDirector: restores("managingDirector")
      ? before.managingDirector
      : current.managingDirector,
    address,
    phones:
      restores("phone") && firstPhone
        ? [{ ...firstPhone, number: firstPhoneNumber(before) }, ...otherPhones]
        : current.phones,
  };
}
